/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef } from 'react';
import Media from 'react-media';

import type { TrackingData } from 'js/lib/retracked';

import { Avatar, ButtonBase, Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import useUserAgent from 'bundles/page/hooks/useUserAgent';

type Props = {
  badge?: JSX.Element;
  name: string;
  logo?: string | null;
  body: JSX.Element;
  buttons?: JSX.Element[];
  courseProgression?: JSX.Element;
  url: string;
  trackingData: TrackingData;
  nameSize?: 'titleSmall' | 'subtitleLarge' | 'subtitleMedium';
};

const styles = {
  mainContent: css`
    display: flex;
    flex-direction: column;
    position: relative;
    gap: var(--cds-spacing-300);
  `,
  wrapper: css`
    border-radius: var(--cds-spacing-300);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    display: flex;
    overflow: hidden;
    height: 100%;
    width: 100%;
    padding-top: var(--cds-spacing-200);
    padding-inline-start: var(--cds-spacing-400);
    padding-inline-end: 0;
    position: relative;
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-200);
    }
  `,
  buttonsContainer: css`
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: flex-start;
    flex-wrap: wrap;
    padding-bottom: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      justify-content: flex-start;
      gap: var(--cds-spacing-100);
      padding-bottom: 0;
    }
  `,
  rowContainer: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    flex-grow: 1;
    gap: var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      margin-right: 0;
      gap: var(--cds-spacing-200);
    }
  `,
  title: css`
    padding-top: var(--cds-spacing-200);
    gap: var(--cds-spacing-150);
    display: flex;
    flex-direction: column;
    ${breakpoints.down('sm')} {
      padding-top: 0;
    }
  `,
  buttonBase: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    align-items: flex-start;

    &:hover,
    &:focus {
      text-decoration: none;

      h3 {
        text-decoration: underline;
      }
    }
  `,
  progressionContainer: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex-grow: 1;
  `,
};

/**
 * @description This component is used as a UI wrapper/structure for the recommended pathway card.
 */
const RecommendedPathwayComponent = forwardRef<HTMLAnchorElement, Props>(
  ({ badge, name, logo, body, buttons, courseProgression, url, trackingData, nameSize = 'titleSmall' }, ref) => {
    const userAgent = useUserAgent();
    const trackV3 = useTracker();

    const visibilityTrack: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_product_card', {
      productCard: trackingData.productCard,
      product: {
        id: trackingData.id,
        slug: trackingData.slug,
      },
      ...(trackingData?.carousel?.id ? { carousel: trackingData.carousel } : {}),
    });

    const handleClick = () => {
      trackV3('click_product_card', {
        product: {
          id: trackingData.id,
          slug: trackingData.slug,
        },
        productCard: trackingData.productCard,
        ...(trackingData?.carousel?.id ? { carousel: trackingData.carousel } : {}),
      });
    };

    return (
      <Grid container css={styles.wrapper} ref={visibilityTrack}>
        {badge}
        <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(isMobileScreen) => {
            return (
              <Grid item xs={12} css={styles.rowContainer}>
                <Grid item xs={12} md={5} css={styles.mainContent}>
                  <ButtonBase component="a" css={styles.buttonBase} href={url} onClick={handleClick} ref={ref}>
                    <div css={styles.title}>
                      {logo && (
                        <Avatar
                          variant="organization"
                          size={24}
                          showBorder={true}
                          imgProps={{
                            src: logo,
                          }}
                        />
                      )}
                      <Typography2 component="h3" variant={nameSize}>
                        {name}
                      </Typography2>
                    </div>
                    {body}
                  </ButtonBase>
                  {isMobileScreen && courseProgression}
                  {buttons && (
                    <Grid item xs={12} css={styles.buttonsContainer}>
                      {buttons?.map((button) => button)}
                    </Grid>
                  )}
                </Grid>
                {!isMobileScreen && (
                  <Grid item xs={12} md={7} css={styles.progressionContainer}>
                    {courseProgression}
                  </Grid>
                )}
              </Grid>
            );
          }}
        </Media>
      </Grid>
    );
  }
);

export default RecommendedPathwayComponent;
